import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminLogin from './components/auth/AdminLogin';
import AdminDashboard from './AdminDashboard';
import PropertyView from './PropertyView';
import VerifyBid from './VerifyBid';
import FAQs from './FAQs';
import EngagementDashboard from './EngagementDashboard';
import { Building2 } from 'lucide-react';
import TutorialOferta from './TutorialOferta';
import SpectatorView from './SpectatorView';
import PublicPropertyView from './PublicPropertyView';

// Despacha entre vista pública (sin token) y vista privada (con token)
const PropertyRouter = (props) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  if (token) return <PropertyView {...props} />;
  return <PublicPropertyView />;
};
import NotificationToast from './components/NotificationToast';

// Importar funciones de Supabase
import { testConnection } from './supabaseClient';
import { 
  getProperties, 
  getBids, 
  getInvites,
  createProperty,
  updateProperty,
  deleteProperty as deletePropertySupabase,
  createInvite,
  deleteInvite,
} from './services/supabaseService';

const App = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [bids, setBids] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPropertyId, setCurrentPropertyId] = useState(null);
  const [currentToken, setCurrentToken] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    initializeApp();
    checkUrlParams();
  }, []);

  // Función helper para notificaciones
  const showNotification = (type, title, message, options = {}) => {
    setNotification({ type, title, message, persistent: options.persistent });

    if (!options.persistent) {
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Inicializar app con verificación de Supabase
  const initializeApp = async () => {
    try {
      console.log('🔄 Verificando conexión a Supabase...');
      const connected = await testConnection();
      
      if (!connected) {
        setConnectionError(true);
        setLoading(false);
        return;
      }

      console.log('✅ Conexión exitosa, cargando datos...');
      await loadData();
    } catch (error) {
      console.error('❌ Error inicializando app:', error);
      setConnectionError(true);
      setLoading(false);
    }
  };

  const checkUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const email = params.get('email');

    // Si hay token y email en la URL, es un magic link
    if (token && email) {
      setCurrentToken(token);
    }
  };

  // Cargar datos desde Supabase
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar datos en paralelo para mayor velocidad
      const [propertiesData, bidsData, invitesData] = await Promise.all([
        getProperties(),
        getBids(),
        getInvites(),
      ]);

      console.log('📦 Datos cargados:', {
        properties: propertiesData.length,
        bids: bidsData.length,
        invites: invitesData.length,
      });

      setProperties(propertiesData);
      setBids(bidsData);
      setInvites(invitesData);
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      showNotification('error', 'Error al cargar los datos', 'Por favor, recarga la página.', { persistent: true });
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: Guardar propiedades - Lógica corregida para distinguir crear vs actualizar
  const saveProperties = async (newProperties) => {
    try {
      // ✅ FIX: Crear un Set con los IDs de propiedades existentes en la BD
      const existingPropertyIds = new Set(properties.map(p => p.id));

      // Separar propiedades nuevas de existentes
      const propsToCreate = [];
      const propsToUpdate = [];

      for (const prop of newProperties) {
        if (prop.id && existingPropertyIds.has(prop.id)) {
          // Es una propiedad existente que hay que actualizar
          propsToUpdate.push(prop);
        } else if (!prop.id || !existingPropertyIds.has(prop.id)) {
          // Es una propiedad nueva (sin id o con id que no existe en BD)
          propsToCreate.push(prop);
        }
      }

      // Crear nuevas propiedades
      for (const prop of propsToCreate) {
        await createProperty(prop);
      }

      // Actualizar propiedades existentes
      for (const prop of propsToUpdate) {
        await updateProperty(prop.id, prop);
      }

      // Identificar propiedades eliminadas
      const newPropertyIds = new Set(newProperties.map(p => p.id).filter(Boolean));
      const deletedProps = properties.filter(p => !newPropertyIds.has(p.id));

      // Eliminar propiedades
      for (const prop of deletedProps) {
        await deletePropertySupabase(prop.id);
      }

      // Recargar datos desde la BD
      const updatedProperties = await getProperties();
      setProperties(updatedProperties);
    } catch (error) {
      console.error('Error guardando propiedades:', error);
      throw error;
    }
  };

  // Guardar ofertas
  const saveBids = async (newBids) => {
    setBids(newBids);
  };

  // Guardar invitaciones
  const saveInvites = async (newInvites) => {
    try {
      const existingInvites = await getInvites();
      
      // Identificar invitaciones eliminadas
      const deletedInvites = existingInvites.filter(
        ei => !newInvites.find(ni => ni.email === ei.email && ni.propertyId === ei.propertyId)
      );

      // Eliminar invitaciones
      for (const invite of deletedInvites) {
        await deleteInvite(invite.id);
      }

      // Crear nuevas invitaciones
      for (const invite of newInvites) {
        const isNewInvite = !existingInvites.find(i => 
          i.email === invite.email && i.propertyId === invite.propertyId
        );
        
        if (isNewInvite) {
          await createInvite(invite);
        }
      }
      
      // Recargar datos
      const updatedInvites = await getInvites();
      setInvites(updatedInvites);
    } catch (error) {
      console.error('Error guardando invitaciones:', error);
      throw error;
    }
  };

  // Función para recargar datos
  const refreshData = async () => {
    await loadData();
  };

  // Mostrar error de conexión
  if (connectionError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-red-500/50 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <Building2 className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Error de Conexión</h2>
          <p className="text-slate-400 mb-4">
            No se pudo conectar a la base de datos. 
          </p>
          <div className="bg-slate-900/50 rounded-lg p-4 mb-4 text-left">
            <p className="text-xs text-slate-400 mb-2">Posibles causas:</p>
            <ul className="text-xs text-slate-300 space-y-1">
              <li>• Verifica tu archivo .env.local</li>
              <li>• Comprueba las credenciales de Supabase</li>
              <li>• Asegúrate de haber ejecutado el SQL</li>
              <li>• Revisa la consola para más detalles (F12)</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
          <div className="text-white text-xl">Cargando...</div>
          <div className="text-slate-400 text-sm mt-2">Conectando a la base de datos</div>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <Routes>
        {/* Ruta raíz redirige a login */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        
        {/* Ruta de login */}
        <Route path="/login" element={<AdminLogin />} />
        
        {/* Rutas protegidas de admin */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard
              properties={properties}
              bids={bids}
              invites={invites}
              onSaveProperties={saveProperties}
              onSaveBids={saveBids}
              onSaveInvites={saveInvites}
              onRefresh={refreshData}
            />
          </ProtectedRoute>
        } />
        
        {/* Rutas de Engagement - Pública para vendedores (seguridad por spectatorToken) */}
        <Route path="/admin/engagement/:propertyId" element={
          <EngagementDashboard properties={properties} />
        } />
        
        {/* /property/:id sin token → vista pública | con token → vista privada */}
        <Route path="/property/:propertyId" element={
          <PropertyRouter
            properties={properties}
            bids={bids}
            invites={invites}
            onSaveBids={saveBids}
            onRefresh={refreshData}
          />
        } />

        {/* Ruta de espectador - sin tracking */}
        <Route path="/property/:propertyId/spectate/:spectatorToken" element={
          <SpectatorView />
        } />

        <Route path="/verify-bid" element={<VerifyBid />} />
        <Route path="/faqs" element={<FAQs />} />
        <Route path="/tutorial-oferta" element={<TutorialOferta />} />
      </Routes>

      {/* Notificación Toast */}
      {notification && (
        <NotificationToast
          type={notification.type}
          title={notification.title}
          message={notification.message}
          persistent={notification.persistent}
          onClose={() => setNotification(null)}
        />
      )}
    </AuthProvider>
  );
};

export default App;